// useUserRoles — fetch the current authenticated user's role grants and
// expose two derived shapes the rest of the portal uses to gate UI:
//
//   roles            — string[] of role names the user holds (legacy shape).
//   grants           — Map<role, "view" | "edit">  per-role permission level
//                      (added 2026-05-04). Defaults every grant to 'edit'
//                      until admins explicitly pick 'view' for a row.
//   isAdmin          — convenience: 'admin' is one of the user's roles.
//   allowedSurfaces  — Set<string> of URL prefixes the user is allowed to
//                      reach. Computed as the union of staff.roles.surfaces
//                      across every role they hold. The AccessGuard + sidebar both
//                      consult this set, so adding more roles to a user
//                      strictly *adds* access — never subtracts.
//   isAllowed(path)  — convenience predicate: does any of the user's role
//                      surfaces match this URL?
//   canEdit(role)    — true when the user has the `role` grant at the
//                      'edit' level. View-only grants return false. Pages
//                      that need fine-grained read/write checks consult
//                      this; pages that only care about presence read
//                      `roles.includes(...)`.
//
// Two round-trips per mount: the caller's own grants (staff.my_roles_with_levels)
// and the role registry (staff.roles, via useRoles() — cached module-side, so
// it is one fetch per page load no matter how many components ask). The hook
// sits high in the tree so the cost is amortised across the page load.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useRoles } from "@/hooks/useRoles";

/**
 * A role name. Roles stopped being a fixed enum in #612 (migration 6048) — they
 * are rows in staff.roles, addable/renameable/retirable from /admin/settings —
 * so this is deliberately a plain string rather than the ten-value union it
 * used to be. Validate against the table (useRoles()), never against a literal
 * list.
 */
export type AppRole = string;

export type PermissionLevel = "view" | "edit";

/**
 * URL prefixes a role grants access to now live in staff.roles.surfaces, read
 * through useRoles(). The portal considers the user's effective access to be
 * the UNION of those arrays across every role they hold. Universal surfaces
 * (login, set-password, docs, security) are always permitted regardless of
 * role and live on UNIVERSAL_SURFACES below.
 *
 * "*" means the role is unrestricted — seeded for `admin` and `all_staff`.
 *
 * This used to be a hardcoded Record<AppRole, string[]> here, which meant a
 * role added from the admin UI could be assigned but could not navigate
 * anywhere. #612 moved the map into the table it describes.
 */

/** Surfaces every signed-in user can reach regardless of role. */
const UNIVERSAL_SURFACES: readonly string[] = [
  "/login",
  "/reset-password",
  "/set-password",
  "/auth",          // MFA challenge + enrollment (#687)
  "/docs",
  "/settings/security",
];

interface RoleRow {
  role: AppRole;
  permission_level: PermissionLevel;
}

interface UseUserRolesResult {
  roles: AppRole[];
  grants: Map<AppRole, PermissionLevel>;
  loading: boolean;
  error: string | null;
  isAdmin: boolean;
  /** Every URL prefix the user can reach. "*" means unrestricted. */
  allowedSurfaces: Set<string>;
  isAllowed: (pathname: string) => boolean;
  canEdit: (role: AppRole) => boolean;
  /**
   * Highest permission level the user has for `pathname`. Returns:
   *   - "edit"  when ANY role with this surface granted at the 'edit' level
   *   - "view"  when at least one role grants the surface but only at 'view'
   *   - null    when no role grants this surface (the user shouldn't be on
   *             this page at all — AccessGuard would redirect first)
   *
   * Max-wins semantics: a user with `kam` (view) + `sales` (edit) gets
   * 'edit' on /customers because sales grants edit and the union covers
   * the path.
   */
  levelForSurface: (pathname: string) => PermissionLevel | null;
  /** Convenience: levelForSurface(path) === 'edit'. */
  canEditSurface: (pathname: string) => boolean;
}

type SurfaceMap = Map<AppRole, readonly string[]>;

/**
 * Fallback when the staff.roles fetch fails outright (network blip, or a portal
 * build that reached users before its migration did). Navigation gating is a
 * UI convenience — the real enforcement is RLS on every table plus
 * requireRole()/requireCapability() in the cc-os-mcp verb layer — so an empty
 * map would lock every user out of a portal whose data they can still legally
 * read, which is strictly worse than falling back to unrestricted navigation
 * and letting the server reject what it should. `error` is still surfaced so
 * the condition is visible rather than silent.
 */
const SURFACE_FALLBACK: readonly string[] = ["*"];

function buildAllowedSurfaces(
  roles: AppRole[],
  surfacesByRole: SurfaceMap,
  degraded: boolean,
): Set<string> {
  const out = new Set<string>(UNIVERSAL_SURFACES);
  for (const r of roles) {
    for (const s of surfacesByRole.get(r) ?? (degraded ? SURFACE_FALLBACK : [])) {
      out.add(s);
    }
  }
  return out;
}

function pathMatches(pathname: string, allowed: Set<string>): boolean {
  if (allowed.has("*")) return true;
  for (const prefix of allowed) {
    if (prefix === "*") return true;
    if (pathname === prefix) return true;
    if (pathname.startsWith(`${prefix}/`)) return true;
  }
  return false;
}

/**
 * For each surface in a role's surface list, decide whether `pathname`
 * matches that surface. Used by `levelForSurface()` to find the most
 * permissive level across all the user's roles.
 */
function roleGrantsSurface(
  role: AppRole,
  pathname: string,
  surfacesByRole: SurfaceMap,
  degraded: boolean,
): boolean {
  const surfaces = surfacesByRole.get(role) ?? (degraded ? SURFACE_FALLBACK : []);
  for (const s of surfaces) {
    if (s === "*") return true;
    if (pathname === s) return true;
    if (pathname.startsWith(`${s}/`)) return true;
  }
  return false;
}

function computeLevelForSurface(
  pathname: string,
  grants: Map<AppRole, PermissionLevel>,
  surfacesByRole: SurfaceMap,
  degraded: boolean,
): PermissionLevel | null {
  // Universal surfaces are always 'view' at minimum (login, docs, etc.)
  // — but we don't carry a 'system' role to mark them with, so callers
  // shouldn't be running canEditSurface on universal paths anyway.
  let best: PermissionLevel | null = null;
  for (const [role, level] of grants) {
    if (!roleGrantsSurface(role, pathname, surfacesByRole, degraded)) continue;
    if (level === "edit") return "edit"; // max wins, short-circuit
    if (best === null) best = "view";
  }
  return best;
}

export function useUserRoles(): UseUserRolesResult {
  const [rows, setRows] = useState<RoleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The role -> surfaces map is data (staff.roles.surfaces) since #612, not a
  // constant in this file. useRoles() caches the fetch module-side, so this
  // costs one request per page load however many components subscribe.
  const { allRoles, loading: rolesLoading, error: rolesError } = useRoles();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Prefer the richer my_roles_with_levels() RPC (added 2026-05-04 in
        // migration 5763). Fall back to legacy my_roles() if the new RPC
        // isn't deployed yet — keeps preview deploys against older DB
        // snapshots from breaking.
        const rich = await Promise.resolve(
          supabase.schema("staff").rpc("my_roles_with_levels"),
        );
        if (cancelled) return;
        if (!rich.error && Array.isArray(rich.data)) {
          setRows(
            (rich.data as Array<{ role: string; permission_level: string }>)
              .map((r) => ({
                role: r.role as AppRole,
                permission_level: (r.permission_level as PermissionLevel) ?? "edit",
              })),
          );
          setLoading(false);
          return;
        }

        const legacy = await Promise.resolve(
          supabase.schema("staff").rpc("my_roles"),
        );
        if (cancelled) return;
        if (legacy.error) {
          setError(legacy.error.message);
          setLoading(false);
          return;
        }
        const list = (Array.isArray(legacy.data) ? legacy.data : []) as string[];
        setRows(list.map((role) => ({ role: role as AppRole, permission_level: "edit" })));
        setLoading(false);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const result = useMemo<UseUserRolesResult>(() => {
    const roles = rows.map((r) => r.role);
    const grants = new Map<AppRole, PermissionLevel>(
      rows.map((r) => [r.role, r.permission_level]),
    );
    const isAdmin = roles.includes("admin");
    const surfacesByRole: SurfaceMap = new Map(
      allRoles.map((r) => [r.name, r.surfaces]),
    );
    // "Degraded" = the registry could not be read at all. See SURFACE_FALLBACK.
    const degraded = rolesError !== null || (!rolesLoading && allRoles.length === 0);
    const allowedSurfaces = buildAllowedSurfaces(roles, surfacesByRole, degraded);
    return {
      roles,
      grants,
      loading: loading || rolesLoading,
      error: error ?? rolesError,
      isAdmin,
      allowedSurfaces,
      isAllowed: (pathname: string) => pathMatches(pathname, allowedSurfaces),
      canEdit: (role: AppRole) => grants.get(role) === "edit",
      levelForSurface: (pathname: string) =>
        computeLevelForSurface(pathname, grants, surfacesByRole, degraded),
      canEditSurface: (pathname: string) =>
        computeLevelForSurface(pathname, grants, surfacesByRole, degraded) === "edit",
    };
  }, [rows, loading, error, allRoles, rolesLoading, rolesError]);

  return result;
}
